import React, { useEffect, useState } from 'react';
import axios from './axiosConfig';
import { motion, AnimatePresence } from 'framer-motion';
import '../App.css';

// Pasos visuales (mismo estilo que BusquedaPublicaciones)
const STEPS = [
  { label: 'Universidad', icon: 'account_balance' },
  { label: 'Entidad', icon: 'category' },
  { label: 'Archivo CSV', icon: 'upload_file' },
  { label: 'Importar', icon: 'done_all' },
];

function Stepper({ currentStep }) {
  return (
    <div className="flex items-center justify-center mb-4">
      {STEPS.map((step, idx) => {
        const stepNumber = idx + 1;
        const isCompleted = currentStep > stepNumber;
        const isActive = currentStep === stepNumber;
        return (
          <React.Fragment key={step.label}>
            <div className="flex flex-col items-center gap-1.5">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm shadow transition-all ${
                  isCompleted
                    ? 'bg-green-500 text-white'
                    : isActive
                    ? 'bg-primary text-white ring-4 ring-blue-200 dark:ring-blue-900'
                    : 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
                }`}
              >
                {isCompleted ? (
                  <span className="material-icons-round text-lg">check</span>
                ) : (
                  <span className="material-icons-round text-lg">{step.icon}</span>
                )}
              </div>
              <span
                className={`text-xs font-semibold ${
                  isActive ? 'text-primary dark:text-blue-300' : 'text-slate-400 dark:text-slate-500'
                }`}
              >
                {step.label}
              </span>
            </div>
            {idx < STEPS.length - 1 && (
              <div
                className={`flex-1 h-0.5 mx-3 mt-[-1rem] transition-all ${
                  currentStep > stepNumber ? 'bg-green-400' : 'bg-slate-200 dark:bg-slate-700'
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

const ENTITY_META = {
  investigadores: {
    icon: 'person',
    label: 'Investigadores',
    hint: 'CSV con datos de investigadores; la universidad del paso 1 se aplicará al cargar.',
  },
  lineas: {
    icon: 'psychology',
    label: 'Líneas de investigación',
    hint: 'CSV con columna nombre (obligatoria); IES del paso 1. Opcional: descripcion, estado. Legado: categoria/subcategoria/keywords → descripción.',
  },
  publicaciones: {
    icon: 'article',
    label: 'Publicaciones',
    hint: 'CSV de publicaciones vinculadas a investigadores existentes.',
  },
  scimago: {
    icon: 'trending_up',
    label: 'SCImago (cuartiles SJR)',
    hint: 'CSV oficial SCImago: separador ; columnas Issn y SJR Best Quartile.',
  },
};

const ENTITIES_WITH_UNIV = ['investigadores', 'lineas'];
const ENTITIES_WITHOUT_UNIV = ['publicaciones', 'scimago'];

function ImportCSV() {
  const [file, setFile] = useState(null);
  const [entity, setEntity] = useState('investigadores');
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1);
  const [universidades, setUniversidades] = useState([]);
  const [loadingUniversidades, setLoadingUniversidades] = useState(false);
  const [selectedUniversidad, setSelectedUniversidad] = useState(null);
  const [progress, setProgress] = useState(0);
  const [successMessage, setSuccessMessage] = useState(null);

  const allowedEntities = selectedUniversidad ? ENTITIES_WITH_UNIV : ENTITIES_WITHOUT_UNIV;

  const resetWizard = (message = null) => {
    setFile(null);
    setEntity('investigadores');
    setResponse(null);
    setError(null);
    setStep(1);
    setSelectedUniversidad(null);
    setProgress(0);
    setSuccessMessage(message);
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setResponse(null);
    setError(null);
    setSuccessMessage(null);
  };

  // Cargar universidades para paso 1
  useEffect(() => {
    setLoadingUniversidades(true);
    axios
      .get('/api/universities')
      .then((res) => setUniversidades(res.data || []))
      .catch((err) => {
        console.error('Error cargando universidades', err);
        setError('No se pudieron cargar las universidades');
      })
      .finally(() => setLoadingUniversidades(false));
  }, []);

  // Si la entidad actual no aplica a la elección de universidad, corregir (p. ej. volver del paso 1)
  useEffect(() => {
    if (step !== 2) return;
    const allowed = selectedUniversidad ? ENTITIES_WITH_UNIV : ENTITIES_WITHOUT_UNIV;
    if (!allowed.includes(entity)) {
      setEntity(allowed[0]);
      setFile(null);
      setResponse(null);
    }
  }, [step, selectedUniversidad, entity]);

  const setEntityChoice = (key) => {
    if (!allowedEntities.includes(key)) return;
    setEntity(key);
    setFile(null);
    setResponse(null);
    setError(null);
    setSuccessMessage(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setResponse(null);
    setError(null);

    // Ejecutar importación (Paso 4)
    if (!file) {
      setError('Por favor, selecciona un archivo CSV');
      return;
    }

    if (entity === 'investigadores' && !selectedUniversidad) {
      setError('Selecciona una universidad para importar investigadores');
      return;
    }
    if (ENTITIES_WITH_UNIV.includes(entity) && !selectedUniversidad) {
      setError('Esta opción requiere universidad en el paso 1.');
      return;
    }
    if (entity === 'scimago' && !file.name.toLowerCase().endsWith('.csv')) {
      setError('SCImago requiere un archivo .csv');
      return;
    }

    setLoading(true);
    setProgress(0);
    const formData = new FormData();
    formData.append('file', file);

    if (entity === 'investigadores') {
      formData.append('universidad_id', String(selectedUniversidad.id));
      formData.append('universidad_nombre', selectedUniversidad.name);
    }
    if (entity === 'lineas') {
      formData.append('universidad_id', String(selectedUniversidad.id));
    }

    const interval = setInterval(() => {
      setProgress((p) => (p >= 90 ? p : p + 3));
    }, 120);

    try {
      if (entity === 'scimago') {
        const res = await axios.post('/api/cuartiles/procesar-scimago', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
        });
        setProgress(100);
        setResponse({ tipo: 'scimago', ...res.data });
      } else {
        const res = await axios.post(`/api/import/${entity}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setProgress(100);
        setResponse({ tipo: 'csv', ...res.data });
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(
        'Error al importar: ' +
          (typeof detail === 'string' ? detail : detail ? JSON.stringify(detail) : err.message)
      );
    } finally {
      clearInterval(interval);
      setTimeout(() => setLoading(false), 400);
    }
  };

  useEffect(() => {
    if (!response) return;

    if (response.tipo === 'scimago') {
      resetWizard('La importación de SCImago terminó correctamente.');
      return;
    }

    if (response.tipo === 'csv') {
      resetWizard(`La importación terminó correctamente. Registros importados: ${response.imported || 0}.`);
    }
  }, [response]);

  return (
    <>
      {/* Header 
      <motion.header 
        className="bg-surface-light dark:bg-surface-dark shadow-sm border-b border-border-light dark:border-border-dark z-10"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="max-w-1xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div>
              <h2 className="text-xl font-bold leading-7 text-slate-900 dark:text-white sm:truncate">Importar Datos desde CSV</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Carga masiva de datos desde archivos CSV al sistema</p>
            </div>
          </div>
        </div>
      </motion.header>*/}

      <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 space-y-6">
        {successMessage && (
          <div className="max-w-5xl mx-auto rounded-xl border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 px-4 py-3 text-sm text-green-700 dark:text-green-300">
            {successMessage}
          </div>
        )}

        {/* Error Alert */}  
        <AnimatePresence>
          {error && (
            <motion.div 
              className="bg-red-50 border border-red-200 rounded-md p-4 mb-6"
              initial={{ opacity: 0, x: -20, scale: 0.95 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 20, scale: 0.95 }}
              transition={{ type: "spring", stiffness: 500, damping: 30 }}
            >
              <div className="flex">
                <motion.span 
                  className="material-icons-round text-red-400 mr-3"
                  animate={{ rotate: [0, -10, 10, -10, 0] }}
                  transition={{ duration: 0.5 }}
                >
                  error
                </motion.span>
                <div>
                  <h3 className="text-sm font-medium text-red-800">Error</h3>
                  <div className="text-sm text-red-700 mt-1">{error}</div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Import Wizard (mismo diseño que BusquedaPublicaciones) */}
        <div className="max-w-5xl mx-auto">
          <div className="mb-6 text-center">
            <h2 className="text-2xl font-extrabold text-slate-900 dark:text-white tracking-tight">
              Importación desde CSV
            </h2>
            <p className="text-slate-500 dark:text-slate-400 mt-2 text-sm">
              Flujo guiado por pasos para cargar datos al sistema
            </p>
          </div>

          {/* Stepper visual */}
          <Stepper currentStep={step} />

          {/* Card container */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-md border border-slate-200 dark:border-slate-800 p-6 md:p-8">
            <AnimatePresence mode="wait">
              {step === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  {/* Encabezado igual a StepSelectUniversidad */}
                  <div className="mb-8 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-100 dark:bg-blue-900/30 mb-4">
                      <span className="material-icons-round text-3xl text-[#2D5BA6]">account_balance</span>
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Seleccionar Universidad</h2>
                    <p className="text-slate-500 dark:text-slate-400 mt-2 max-w-xl mx-auto">
                      Si eliges una universidad, en el siguiente paso podrás importar{' '}
                      <strong>Investigadores</strong> o <strong>Líneas de investigación</strong>. Si omites, podrás
                      importar <strong>Publicaciones</strong> o el CSV de <strong>SCImago</strong> (cuartiles).
                    </p>
                  </div>

                  {loadingUniversidades ? (
                    <div className="flex items-center justify-center py-16">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-10 h-10 border-4 border-[#2D5BA6] border-t-transparent rounded-full animate-spin" />
                        <span className="text-slate-500 dark:text-slate-400">Cargando universidades...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {/* Tarjeta Omitir */}
                      <motion.button
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.02 }}
                        type="button"
                        onClick={() => {
                          setSelectedUniversidad(null);
                          setStep(2);
                        }}
                        className="group flex flex-col items-start gap-3 p-5 bg-white dark:bg-slate-800 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl hover:border-[#2D5BA6] dark:hover:border-[#5B8BD9] hover:shadow-lg transition-all text-left"
                      >
                        <div className="flex items-center gap-3 w-full">
                          <div className="w-10 h-10 rounded-lg bg-slate-50 dark:bg-slate-700 flex items-center justify-center flex-shrink-0 group-hover:bg-slate-100 dark:group-hover:bg-slate-600 transition-colors">
                            <span className="material-icons-round text-xl text-slate-500">block</span>
                          </div>
                          <div className="min-w-0">
                            <p className="font-bold text-slate-900 dark:text-white text-sm truncate">Omitir</p>
                            <p className="text-xs text-slate-500 dark:text-slate-400">No aplicar universidad</p>
                          </div>
                        </div>
                        <p className="text-xs text-slate-600 dark:text-slate-300 leading-snug line-clamp-2">
                          Activa las opciones Publicaciones y SCImago (cuartiles) en el siguiente paso.
                        </p>
                        <div className="flex items-center gap-1 text-slate-500 dark:text-slate-300 text-xs font-semibold mt-auto group-hover:gap-2 transition-all">
                          <span>Continuar sin universidad</span>
                          <span className="material-icons-round text-base">arrow_forward</span>
                        </div>
                      </motion.button>

                      {universidades.map((univ, idx) => (
                        <motion.button
                          key={univ.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.05 * (idx + 1) }}
                          onClick={() => {
                            setSelectedUniversidad(univ);
                            setStep(2);
                          }}
                          className={`group flex flex-col items-start gap-3 p-5 bg-white dark:bg-slate-800 border-2 rounded-xl hover:border-[#2D5BA6] dark:hover:border-[#5B8BD9] hover:shadow-lg transition-all text-left ${
                            selectedUniversidad?.id === univ.id
                              ? 'border-[#2D5BA6] dark:border-[#5B8BD9]'
                              : 'border-slate-200 dark:border-slate-700'
                          }`}
                        >
                          <div className="flex items-center gap-3 w-full">
                            <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/40 transition-colors">
                              <span className="material-icons-round text-xl text-[#2D5BA6]">school</span>
                            </div>
                            <div className="min-w-0">
                              <p className="font-bold text-slate-900 dark:text-white text-sm truncate">
                                {univ.siglas || univ.short_name}
                              </p>
                              <p className="text-xs text-slate-500 dark:text-slate-400">{univ.ciudad || univ.country}</p>
                            </div>
                          </div>
                          <p className="text-xs text-slate-600 dark:text-slate-300 leading-snug line-clamp-2">
                            {univ.name}
                          </p>
                          <div className="flex items-center gap-1 text-[#2D5BA6] dark:text-[#5B8BD9] text-xs font-semibold mt-auto group-hover:gap-2 transition-all">
                            <span>Seleccionar</span>
                            <span className="material-icons-round text-base">arrow_forward</span>
                          </div>
                        </motion.button>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}

              {step === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="mb-6">
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      className="flex items-center gap-1 text-sm text-slate-500 dark:text-slate-400 hover:text-primary transition-colors mb-4"
                    >
                      <span className="material-icons-round text-base">arrow_back</span>
                      Volver
                    </button>
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white">Tipo de importación</h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                      {selectedUniversidad ? (
                        <>
                          Universidad:{' '}
                          <strong className="text-slate-700 dark:text-slate-200">
                            {selectedUniversidad.siglas || selectedUniversidad.short_name || selectedUniversidad.name}
                          </strong>
                          . Solo aplica investigadores o líneas.
                        </>
                      ) : (
                        <>Sin universidad: puedes cargar publicaciones o el ranking SCImago para cuartiles.</>
                      )}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {allowedEntities.map((key) => {
                      const meta = ENTITY_META[key];
                      const active = entity === key;
                      return (
                        <motion.button
                          key={key}
                          type="button"
                          initial={{ opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }}
                          onClick={() => setEntityChoice(key)}
                          className={`group flex flex-col items-start gap-3 p-5 bg-white dark:bg-slate-800 border-2 rounded-xl text-left transition-all hover:shadow-lg ${
                            active
                              ? 'border-[#2D5BA6] dark:border-[#5B8BD9] ring-2 ring-[#2D5BA6]/20'
                              : 'border-slate-200 dark:border-slate-700 hover:border-[#2D5BA6]/60 dark:hover:border-[#5B8BD9]/60'
                          }`}
                        >
                          <div className="flex items-center gap-3 w-full">
                            <div
                              className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
                                active
                                  ? 'bg-[#2D5BA6] text-white'
                                  : 'bg-blue-50 dark:bg-blue-900/25 text-[#2D5BA6]'
                              }`}
                            >
                              <span className="material-icons-round text-2xl">{meta.icon}</span>
                            </div>
                            <div className="min-w-0">
                              <p className="font-bold text-slate-900 dark:text-white">{meta.label}</p>
                              {active && (
                                <span className="text-xs font-semibold text-[#2D5BA6] dark:text-[#5B8BD9]">
                                  Seleccionado
                                </span>
                              )}
                            </div>
                          </div>
                          <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">{meta.hint}</p>
                          <div className="flex items-center gap-1 text-[#2D5BA6] dark:text-[#5B8BD9] text-xs font-semibold mt-auto opacity-0 group-hover:opacity-100 transition-opacity">
                            <span>Elegir</span>
                            <span className="material-icons-round text-base">touch_app</span>
                          </div>
                        </motion.button>
                      );
                    })}
                  </div>

                  <div className="mt-8 flex justify-end">
                    <button
                      type="button"
                      className="px-5 py-2.5 rounded-lg bg-primary text-white font-semibold hover:bg-blue-600 transition-colors"
                      onClick={() => {
                        if (!allowedEntities.includes(entity)) {
                          setError('Selecciona un tipo de importación válido.');
                          return;
                        }
                        setStep(3);
                      }}
                    >
                      Continuar
                    </button>
                  </div>
                </motion.div>
              )}

              {step === 3 && (
                <motion.div
                  key="step3"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="mb-6">
                    <button
                      type="button"
                      onClick={() => setStep(2)}
                      className="flex items-center gap-1 text-sm text-slate-500 dark:text-slate-400 hover:text-primary transition-colors mb-4"
                    >
                      <span className="material-icons-round text-base">arrow_back</span>
                      Volver
                    </button>
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white">Subir CSV</h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                      {entity === 'scimago' ? (
                        <>
                          Archivo <strong>.csv</strong> de SCImago (separador <strong>;</strong>), con columnas{' '}
                          <code className="text-xs bg-slate-100 dark:bg-slate-800 px-1 rounded">Issn</code> y{' '}
                          <code className="text-xs bg-slate-100 dark:bg-slate-800 px-1 rounded">SJR Best Quartile</code>.
                        </>
                      ) : (
                        <>Selecciona el archivo CSV para {ENTITY_META[entity]?.label?.toLowerCase()}.</>
                      )}
                    </p>
                  </div>

                  <motion.div
                    className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
                      file
                        ? 'border-green-300 bg-green-50 dark:bg-green-900/20'
                        : 'border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 hover:border-slate-400 dark:hover:border-slate-500'
                    }`}
                    whileHover={{ scale: 1.01, borderColor: '#3b82f6' }}
                    whileTap={{ scale: 0.99 }}
                  >
                    <input type="file" accept=".csv" onChange={handleFileChange} className="hidden" id="csv-upload" />
                    <label htmlFor="csv-upload" className="cursor-pointer">
                      <div className={`text-4xl mb-3 ${file ? 'text-green-500' : 'text-slate-400'}`}>
                        <span className="material-icons-round text-4xl">{file ? 'check_circle' : 'cloud_upload'}</span>
                      </div>
                      <p className="text-slate-900 dark:text-white font-medium mb-1">
                        {file ? file.name : 'Haz clic para seleccionar archivo'}
                      </p>
                      <p className="text-slate-500 dark:text-slate-400 text-sm">
                        {file ? `${(file.size / 1024).toFixed(2)} KB` : 'Solo archivos .csv'}
                      </p>
                    </label>
                  </motion.div>

                  <div className="mt-6 flex justify-end">
                    <button
                      type="button"
                      className="px-5 py-2.5 rounded-lg bg-primary text-white font-semibold hover:bg-blue-600 transition-colors"
                      onClick={() => {
                        if (!file) {
                          setError('Selecciona un archivo CSV para continuar');
                          return;
                        }
                        setStep(4);
                      }}
                    >
                      Continuar
                    </button>
                  </div>
                </motion.div>
              )}

              {step === 4 && (
                <motion.div
                  key="step4"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="mb-6">
                    <button
                      type="button"
                      onClick={() => setStep(3)}
                      className="flex items-center gap-1 text-sm text-slate-500 dark:text-slate-400 hover:text-primary transition-colors mb-4"
                      disabled={loading}
                    >
                      <span className="material-icons-round text-base">arrow_back</span>
                      Volver
                    </button>
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white">Importar</h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                      {entity === 'scimago'
                        ? 'Se actualizarán los cuartiles de las publicaciones con ISSN coincidente en el listado SCImago.'
                        : 'Ejecuta la carga en la base de datos.'}
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <motion.button
                      type="submit"
                      disabled={loading}
                      className="w-full inline-flex items-center justify-center px-4 py-3 bg-primary text-white rounded-xl hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-semibold"
                      whileHover={{ scale: loading ? 1 : 1.01 }}
                      whileTap={{ scale: loading ? 1 : 0.99 }}
                    >
                      {loading ? (
                        <>
                          <motion.span
                            className="material-icons-round text-sm mr-2"
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                          >
                            sync
                          </motion.span>
                          Importando...
                        </>
                      ) : (
                        <>
                          <span className="material-icons-round text-sm mr-2">rocket_launch</span>
                          Iniciar Importación
                        </>
                      )}
                    </motion.button>

                    {loading && (
                      <div className="flex items-center justify-center pt-4">
                        <div className="relative w-16 h-16">
                          <div className="absolute inset-0 rounded-full border-4 border-slate-200 dark:border-slate-700" />
                          <div className="absolute inset-0 rounded-full border-4 border-primary border-t-transparent animate-spin" />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                              {progress}%
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </form>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
        {/* Instrucciones 
        <section>
          <div className="flex items-center justify-between mb-4 px-1">
            <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Formato Requerido por Entidad</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="glass-panel rounded-xl p-6 border border-white/20 shadow-lg dark:border-slate-700 relative overflow-hidden group hover:scale-[1.01] transition-transform duration-200">
              <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                <span className="material-icons-round text-6xl text-primary">person</span>
              </div>
              <div className="flex items-center mb-3">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                  Investigadores
                </span>
              </div>
              <h4 className="text-md font-semibold text-slate-800 dark:text-slate-100 mb-2">Columnas requeridas</h4>
              <div className="text-xs font-mono text-slate-600 dark:text-slate-400 bg-blue-50 dark:bg-blue-900/20 p-3 rounded">
                nombre, genero, institucion, tipo_puesto, orcid, email, universidad_id (opcional)
              </div>
            </div>
            <div className="glass-panel rounded-xl p-6 border border-white/20 shadow-lg dark:border-slate-700 relative overflow-hidden group hover:scale-[1.01] transition-transform duration-200">
              <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                <span className="material-icons-round text-6xl text-green-500">article</span>
              </div>
              <div className="flex items-center mb-3">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                  Publicaciones
                </span>
              </div>
              <h4 className="text-md font-semibold text-slate-800 dark:text-slate-100 mb-2">Columnas requeridas</h4>
              <div className="text-xs font-mono text-slate-600 dark:text-slate-400 bg-green-50 dark:bg-green-900/20 p-3 rounded">
                titulo, año, revista, impact_factor, keywords, abstract, doi, investigador_id
              </div>
            </div>
            <div className="glass-panel rounded-xl p-6 border border-white/20 shadow-lg dark:border-slate-700 relative overflow-hidden group hover:scale-[1.01] transition-transform duration-200">
              <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                <span className="material-icons-round text-6xl text-purple-500">psychology</span>
              </div>
              <div className="flex items-center mb-3">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
                  Líneas de Investigación
                </span>
              </div>
              <h4 className="text-md font-semibold text-slate-800 dark:text-slate-100 mb-2">Columnas requeridas</h4>
              <div className="text-xs font-mono text-slate-600 dark:text-slate-400 bg-purple-50 dark:bg-purple-900/20 p-3 rounded">
                nombre, categoria_principal, subcategoria, descripcion, keywords
              </div>
            </div>
          </div>
        </section>*/}

        {/* Resultados de Importación */}
        {response && response.tipo === 'scimago' && (
          <section className="bg-surface-light dark:bg-surface-dark shadow-md rounded-xl border border-border-light dark:border-border-dark overflow-hidden max-w-5xl mx-auto">
            <div className="px-6 py-4 border-b border-border-light dark:border-border-dark flex items-center justify-between bg-slate-50/50 dark:bg-slate-800/50">
              <h3 className="text-base font-semibold text-slate-900 dark:text-white flex items-center">
                <span className="material-icons-round mr-2 text-emerald-500">trending_up</span>
                SCImago — cuartiles actualizados
              </h3>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200">
                Completado
              </span>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 bg-white dark:bg-slate-800/50">
                  <div className="text-slate-500 dark:text-slate-400 text-xs">Revistas en CSV</div>
                  <div className="text-2xl font-bold text-slate-900 dark:text-white">{response.total_revistas}</div>
                </div>
                <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 bg-white dark:bg-slate-800/50">
                  <div className="text-slate-500 dark:text-slate-400 text-xs">Publicaciones revisadas con ISSN</div>
                  <div className="text-2xl font-bold text-slate-900 dark:text-white">{response.revistas_procesadas}</div>
                </div>
                <div className="rounded-xl border border-emerald-200 dark:border-emerald-800 p-4 bg-emerald-50/80 dark:bg-emerald-900/20">
                  <div className="text-emerald-800 dark:text-emerald-300 text-xs">Cuartil asignado</div>
                  <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                    {response.publicaciones_actualizadas}
                  </div>
                </div>
                <div className="rounded-xl border border-amber-200 dark:border-amber-800 p-4 bg-amber-50/80 dark:bg-amber-900/20">
                  <div className="text-amber-800 dark:text-amber-300 text-xs">Sin match en CSV</div>
                  <div className="text-2xl font-bold text-amber-700 dark:text-amber-300">
                    {response.publicaciones_sin_cuartil}
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {response && response.tipo === 'csv' && (
          <section className="bg-surface-light dark:bg-surface-dark shadow-md rounded-xl border border-border-light dark:border-border-dark overflow-hidden max-w-5xl mx-auto">
            <div className="px-6 py-4 border-b border-border-light dark:border-border-dark flex items-center justify-between bg-slate-50/50 dark:bg-slate-800/50">
              <h3 className="text-base font-semibold text-slate-900 dark:text-white flex items-center">
                <span className="material-icons-round mr-2 text-green-500">check_circle</span>
                Resultado de Importación
              </h3>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200">
                Completado
              </span>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="glass-panel rounded-xl p-6 border border-white/20 shadow-lg dark:border-slate-700 relative overflow-hidden group hover:scale-[1.01] transition-transform duration-200 bg-green-50 dark:bg-green-900/20">
                  <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                    <span className="material-icons-round text-6xl text-green-500">check_circle</span>
                  </div>
                  <div className="flex items-center mb-3">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                      Registros Importados
                    </span>
                  </div>
                  <h4 className="text-3xl font-bold text-green-600 dark:text-green-400 mb-2">{response.imported}</h4>
                  <div className="flex items-center justify-between border-t border-green-200 dark:border-green-700/50 pt-3">
                    <span className="text-xs font-medium text-green-600">Procesados exitosamente</span>
                  </div>
                </div>
                {response.duplicates_or_errors?.length > 0 && (
                  <div className="glass-panel rounded-xl p-6 border border-white/20 shadow-lg dark:border-slate-700 bg-red-50 dark:bg-red-900/20">
                    <div className="flex items-center mb-3">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                        Errores/Duplicados
                      </span>
                    </div>
                    <div className="space-y-2">
                      {response.duplicates_or_errors.slice(0, 5).map((err, i) => (
                        <div key={i} className="flex items-start text-sm text-red-700 dark:text-red-300">
                          <span className="material-icons-round text-xs mr-1 mt-0.5">info</span>
                          <span className="truncate">{err}</span>
                        </div>
                      ))}
                      {response.duplicates_or_errors.length > 5 && (
                        <div className="flex items-start text-sm text-red-700 dark:text-red-300">
                          <span className="material-icons-round text-xs mr-1 mt-0.5">more_horiz</span>
                          <span>... y {response.duplicates_or_errors.length - 5} más</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  );
}

export default ImportCSV;
